import React, { useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { resolveMediaUrl } from "@/lib/api";
import {
  FolderLock, Upload, Loader2, FileText, Trash2, ExternalLink,
  FileImage, FileArchive, Image as ImageIcon, ShieldCheck,
} from "lucide-react";

/**
 * Per-candidate documents vault.
 *
 * @param candidateId  – required, string UUID
 * @param readOnly     – when true renders a view-only list (employer / public
 *                       CandidateDetail). Otherwise renders upload + delete
 *                       controls (Admin CMS).
 * @param title        – optional card title override
 */
export default function CandidateDocumentsSection({ candidateId, readOnly = false, title }) {
  const qc = useQueryClient();
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [category, setCategory] = useState("");

  const { data, isLoading } = useQuery({
    queryKey: ["candidate-documents", candidateId],
    queryFn: async () => (await api.get(`/candidates/${candidateId}/documents`)).data,
    enabled: !!candidateId,
  });

  const documents = data?.documents || [];

  const upload = async (files) => {
    if (!files || files.length === 0) return;
    setUploading(true);
    try {
      const fd = new FormData();
      Array.from(files).forEach((f) => fd.append("files", f));
      if (category) fd.append("category", category);
      const { data: resp } = await api.post(
        `/admin/candidates/${candidateId}/documents`,
        fd,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      toast.success(`${resp.uploaded} document${resp.uploaded === 1 ? "" : "s"} uploaded`);
      qc.invalidateQueries({ queryKey: ["candidate-documents", candidateId] });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const remove = async (doc) => {
    if (!window.confirm(`Delete ${doc.filename}? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/candidates/${candidateId}/documents/${doc.id}`);
      toast.success("Document deleted");
      qc.invalidateQueries({ queryKey: ["candidate-documents", candidateId] });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    upload(e.dataTransfer.files);
  };

  return (
    <div className="rounded-2xl border border-zinc-200 bg-white p-4 md:p-5" data-testid="candidate-documents-section">
      <div className="flex items-start gap-3 mb-3">
        <div className="w-10 h-10 rounded-full bg-[#b8860b]/10 border border-[#b8860b]/30 flex items-center justify-center shrink-0">
          {readOnly ? <ShieldCheck size={16} className="text-[#8b6508]" /> : <FolderLock size={16} className="text-[#8b6508]" />}
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-serif text-lg leading-tight text-zinc-950">
            {title || (readOnly ? "Documents (view-only)" : "Documents Vault")}
          </div>
          <div className="text-[12px] text-zinc-600 mt-0.5 leading-relaxed">
            {readOnly
              ? "Passport, ID card, certificates, and other supporting papers verified by our team. Tap to view."
              : "Upload passport, ID card, family card, CV, certificates. Files remain private; employers can view but not download by default."}
          </div>
        </div>
        {isLoading && <Loader2 size={16} className="animate-spin text-zinc-400" />}
      </div>

      {!readOnly && (
        <>
          <div className="mb-2 flex items-center gap-2">
            <label className="text-[11px] text-zinc-600 uppercase tracking-wider">Category</label>
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              className="text-[12px] h-8 px-2 rounded-md border border-zinc-300 bg-white"
              data-testid="doc-category-select"
            >
              <option value="">Auto-detect from filename</option>
              <option value="passport">Passport</option>
              <option value="id_card">ID Card</option>
              <option value="family_card">Family Card</option>
              <option value="birth_certificate">Birth Certificate</option>
              <option value="cv">CV / Resume</option>
              <option value="certificate">Certificate</option>
              <option value="medical">Medical</option>
              <option value="visa">Visa</option>
              <option value="contract">Contract</option>
              <option value="photo">Photo</option>
              <option value="other">Other</option>
            </select>
          </div>
          <div
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={onDrop}
            onClick={() => inputRef.current?.click()}
            className={`rounded-xl border-2 border-dashed p-6 text-center cursor-pointer transition-all ${dragOver ? "border-[#b8860b] bg-[#b8860b]/5" : "border-zinc-300 hover:border-zinc-500 bg-zinc-50/50"}`}
            data-testid="doc-dropzone"
          >
            {uploading ? (
              <Loader2 size={26} className="mx-auto text-[#b8860b] animate-spin mb-2" />
            ) : (
              <Upload size={26} className="mx-auto text-zinc-400 mb-2" />
            )}
            <div className="text-sm font-medium text-zinc-800">
              {uploading ? "Uploading…" : (dragOver ? "Release to add" : "Drag files here or click to browse")}
            </div>
            <div className="text-[11px] text-zinc-500 mt-1">PDF, JPG, PNG, WEBP · unlimited files per upload</div>
            <input
              ref={inputRef}
              type="file"
              multiple
              accept="image/*,.pdf,.doc,.docx"
              onChange={(e) => upload(e.target.files)}
              className="hidden"
              data-testid="doc-file-input"
            />
          </div>
        </>
      )}

      {/* Documents list */}
      <ul className="mt-4 divide-y divide-zinc-100 border border-zinc-100 rounded-xl overflow-hidden" data-testid="doc-list">
        {documents.length === 0 && (
          <li className="px-4 py-6 text-center text-[12.5px] text-zinc-500 italic">
            No documents attached yet.
          </li>
        )}
        {documents.map((doc) => (
          <li key={doc.id} className="flex items-center gap-3 px-3 py-2.5 hover:bg-zinc-50" data-testid={`doc-row-${doc.id}`}>
            <DocIcon contentType={doc.content_type} />
            <div className="flex-1 min-w-0">
              <div className="text-[13px] font-medium text-zinc-950 truncate" title={doc.filename}>{doc.filename}</div>
              <div className="text-[10.5px] text-zinc-500 flex items-center gap-2 mt-0.5">
                <span className="uppercase tracking-wider bg-zinc-100 px-1.5 py-0.5 rounded-full">{doc.category?.replace(/_/g, " ") || "other"}</span>
                <span>{formatSize(doc.size)}</span>
                <span className="hidden sm:inline">{formatDate(doc.uploaded_at)}</span>
              </div>
            </div>
            <a
              href={resolveMediaUrl(doc.storage_path)}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-[11.5px] text-[#8b6508] hover:text-[#b8860b] hover:underline font-semibold"
              data-testid={`doc-view-${doc.id}`}
            >
              <ExternalLink size={12} /> View
            </a>
            {!readOnly && (
              <button
                type="button"
                onClick={() => remove(doc)}
                className="text-red-600 hover:text-red-800 p-1"
                title="Delete"
                data-testid={`doc-delete-${doc.id}`}
              >
                <Trash2 size={14} />
              </button>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

function DocIcon({ contentType }) {
  const cls = "w-9 h-9 rounded-lg flex items-center justify-center shrink-0";
  if ((contentType || "").startsWith("image/")) return (
    <div className={`${cls} bg-emerald-50 text-emerald-700 border border-emerald-200`}><FileImage size={16} /></div>
  );
  if ((contentType || "").includes("pdf")) return (
    <div className={`${cls} bg-red-50 text-red-700 border border-red-200`}><FileText size={16} /></div>
  );
  if ((contentType || "").includes("zip") || (contentType || "").includes("archive")) return (
    <div className={`${cls} bg-amber-50 text-amber-700 border border-amber-200`}><FileArchive size={16} /></div>
  );
  return (
    <div className={`${cls} bg-zinc-100 text-zinc-700 border border-zinc-200`}><ImageIcon size={16} /></div>
  );
}

function formatSize(bytes) {
  if (!bytes) return "0 KB";
  if (bytes > 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  return `${Math.max(1, Math.round(bytes / 1024))} KB`;
}

function formatDate(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
  } catch { return ""; }
}
